import React from "react"
import { Link, useLocation } from "react-router-dom"
import {
  BrandFacebookIcon,
  BrandInstagramIcon,
  BrandTiktokIcon,
  BrandXIcon,
  BrandYoutubeIcon,
  MailIcon,
} from "@dc-ireland/ui"
import {
  CHAPTER_REGISTRY,
  chapterPath,
  getActiveChapterSlug,
  getChapterMeta,
  getChapterSocialIcons,
  type ChapterSocialBrand,
} from "../../lib/chapters"
import { SOCIAL_BRAND_LABELS, type SocialBrand } from "../SocialIcons"
import { PRAYER_REQUEST_HASH, TESTIMONY_HASH } from "../../lib/prayerTestimony"

const DC_GLOBAL_HREF = "https://dominioncity.cc"
const DEVOTIONAL_HREF = "https://t.me/dominionmandate1"
const CONTACT_EMAIL = "info@dominioncity.ie"

const sectionHeadingClassName =
  "font-dm-sans text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9e7e43]"

const linkClassName =
  "inline-flex items-center gap-2 bg-transparent p-0 font-dm-sans text-sm text-[#f5f5f5] transition-colors hover:text-[#9e7e43]"

const emailLinkClassName =
  "inline-flex items-center gap-1.5 font-dm-sans text-xs tracking-wide text-[#7a92b0] transition-opacity hover:underline hover:underline-offset-2 hover:opacity-80"

const columnClassName =
  "flex flex-col gap-4 max-[720px]:items-center max-[720px]:text-center"

const SocialBrandIcon = ({ brand, size = 16 }: { brand: ChapterSocialBrand; size?: number }) => {
  switch (brand) {
    case "youtube":
      return <BrandYoutubeIcon size={size} />
    case "instagram":
      return <BrandInstagramIcon size={size} />
    case "facebook":
      return <BrandFacebookIcon size={size} />
    case "tiktok":
      return <BrandTiktokIcon size={size} />
    case "x":
      return <BrandXIcon size={size} />
  }
}

type ConnectSocial = {
  brand: ChapterSocialBrand
  href: string
  label: string
}

const LandingFooter: React.FC = () => {
  const { pathname } = useLocation()
  const isHomePage = pathname === "/"
  const activeSlug = getActiveChapterSlug(pathname)
  const activeChapter = activeSlug ? getChapterMeta(activeSlug) : undefined
  const branches = CHAPTER_REGISTRY.filter((chapter) => chapter.slug !== activeSlug)

  const chapterEmail = activeSlug ? `${activeSlug}@dominioncity.ie` : undefined

  const connectSocials: ConnectSocial[] = activeChapter
    ? getChapterSocialIcons(activeChapter.socials).map(({ brand, href }) => ({
        brand,
        href,
        label: SOCIAL_BRAND_LABELS[brand as SocialBrand],
      }))
    : []

  return (
    <footer className="relative overflow-hidden bg-[#00205c] px-4 py-10 text-white md:px-8 lg:px-20">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#9e7e43]/40 to-transparent" />
      <div className="mx-auto max-w-[1240px]">
        <div className="grid grid-cols-3 gap-4 sm:gap-6 lg:gap-12 max-[720px]:grid-cols-1 max-[720px]:gap-10">
          <div className={columnClassName}>
            <div className="flex items-center gap-3 max-[720px]:justify-center">
              <Link to="/">
                <img
                  src="/dominion-city-logo.png"
                  alt="Dominion City Logo"
                  className="h-10 w-10 object-contain"
                />
              </Link>
              <div>
                <p className="font-outfit text-sm font-semibold uppercase tracking-wide text-white">
                  Dominion City
                </p>
                <p className="font-outfit text-xs font-medium uppercase tracking-[0.18em] text-[#9e7e43]">
                  Ireland
                </p>
              </div>
            </div>

            <p className="max-w-sm font-dm-sans text-sm leading-6 tracking-wide text-[#f5f5f5] max-[720px]:mx-auto">
              Raising leaders that transform society through faith, community, and divine purpose across
              Ireland.
            </p>

            <div className="flex flex-col gap-1 max-[720px]:items-center">
              <p className="font-dm-sans text-xs tracking-wide text-[#7a92b0]">
                Dominion City Ireland CLG
              </p>
              <a href={`mailto:${CONTACT_EMAIL}`} className={emailLinkClassName}>
                <MailIcon size={14} />
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>

          <div className={columnClassName}>
            <p className={sectionHeadingClassName}>Our Branches</p>
            <ul className="flex flex-wrap items-center gap-y-2 max-[720px]:justify-center">
              {branches.map((chapter) => (
                <li
                  key={chapter.slug}
                  className="after:mx-2 after:text-[#7a92b0] after:content-['·'] last:after:content-none"
                >
                  <Link to={chapterPath(chapter.slug)} className={linkClassName}>
                    {chapter.name}
                  </Link>
                </li>
              ))}
            </ul>

            <div aria-hidden="true" className="h-px w-full bg-[#003699]" />

            <ul className="flex flex-col gap-3 max-[720px]:items-center">
              <li>
                <Link to={{ hash: PRAYER_REQUEST_HASH }} className={linkClassName}>
                  Send a prayer request
                </Link>
              </li>
              <li>
                <Link to={{ hash: TESTIMONY_HASH }} className={linkClassName}>
                  Share a testimony
                </Link>
              </li>
              {chapterEmail ? (
                <li>
                  <a href={`mailto:${chapterEmail}`} className={emailLinkClassName}>
                    <MailIcon size={14} />
                    {chapterEmail}
                  </a>
                </li>
              ) : null}
            </ul>
          </div>

          <div className={columnClassName}>
            <p className={sectionHeadingClassName}>Connect</p>
            <ul className="flex flex-col gap-3 max-[720px]:items-center">
              <li>
                <a
                  href={DC_GLOBAL_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClassName}
                >
                  DC Global Website
                </a>
              </li>

              {isHomePage ? (
                <li>
                  <a
                    href={DEVOTIONAL_HREF}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClassName}
                  >
                    Devotional
                  </a>
                </li>
              ) : null}

              {connectSocials.map(({ brand, href, label }) => (
                <li key={`${brand}-${href}`}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={label}
                    aria-label={label}
                    className={linkClassName}
                  >
                    <SocialBrandIcon brand={brand} />
                    <span>{label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="my-8 border-t border-[#003699]" />

        <div className="flex flex-col gap-3 text-center font-dm-sans text-xs leading-relaxed tracking-wide text-[#7a92b0]">
          <p className="mx-auto w-full max-w-4xl text-center">
            Dominion City Ireland CLG is registered with the Companies Registration Office (CRO) as a Company Limited by
            Guarantee (CLG) and with the Charities Regulator as a Charity with CRO Number: 726325 and Registered Charity
            number: 20206477
          </p>

          <div className="flex flex-col gap-2 min-[721px]:flex-row min-[721px]:items-center min-[721px]:justify-between">
            <p>&copy; {new Date().getFullYear()} Dominion City Ireland CLG. All rights reserved.</p>
            <a
              href={DC_GLOBAL_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
            >
              Part of Dominion City Global
            </a>
          </div>
        </div>
      </div>

    </footer>
  )
}

export default LandingFooter
